import { useReducer, useRef, useEffect } from 'react';
import type { CalculatorFormData, CalculatorResult } from './calculatorTypes';
import { defaultFormData, MAX_STEP_SEQUENCE } from './calculatorTypes';
import type { CalculatorState, CalculatorAction } from './calculatorTypes';
import { steps, getStepById, resolveNextStep } from './calculatorUtils';
import { calculateApi } from '../../api/api';
import { IncomeStep } from './steps/IncomeStep';
import { YesNoStep } from './steps/YesNoStep';
import { StepperGroupsStep } from './steps/StepperGroupsStep';
import { MultiIncomeStep } from './steps/MultiIncomeStep';
import { HousingStep } from './steps/HousingStep';
import { ChildAgesStep } from './steps/ChildAgesStep';
import { SummaryStep } from './steps/SummaryStep';
import buttonCloseTransparent from '../../assets/button_close_transparent.svg';
import { getTexts } from './locales';
import styles from './Calculator.module.css';

interface Props {
  onClose: () => void;
  onResult: (result: CalculatorResult) => void;
  onStepChange?: () => void;
  lang?: string;
}

const initialState: CalculatorState = {
  stepHistory: [steps[0].id],
  formData: defaultFormData,
  isLoading: false,
  showCancelModal: false,
  validationErrors: {},
  apiError: null,
};

function reducer(state: CalculatorState, action: CalculatorAction): CalculatorState {
  switch (action.type) {
    case 'PUSH_STEP':
      return { ...state, stepHistory: [...state.stepHistory, action.stepId], apiError: null };
    case 'POP_STEP':
      if (state.stepHistory.length <= 1) return state;
      return { ...state, stepHistory: state.stepHistory.slice(0, -1), apiError: null };
    case 'UPDATE_FORM':
      return { ...state, formData: { ...state.formData, ...action.data } };
    case 'SET_LOADING':
      return { ...state, isLoading: action.value };
    case 'SHOW_CANCEL_MODAL':
      return { ...state, showCancelModal: action.value };
    case 'SET_ERRORS':
      return { ...state, validationErrors: action.errors };
    case 'SET_API_ERROR':
      return { ...state, apiError: action.error, isLoading: false };
    default:
      return state;
  }
}

function getProgress(currentStepId: string): number {
  const idx = MAX_STEP_SEQUENCE.indexOf(
    currentStepId as (typeof MAX_STEP_SEQUENCE)[number]
  );
  if (idx === -1) return 0;
  return idx / (MAX_STEP_SEQUENCE.length - 1);
}

export function Calculator({ onClose, onResult, onStepChange, lang }: Props) {
  const texts = getTexts(lang);
  const [state, dispatch] = useReducer(reducer, initialState);
  
  const currentStepId = state.stepHistory[state.stepHistory.length - 1];
  const currentStep = getStepById(currentStepId);
  const canGoBack = state.stepHistory.length > 1;
  const progress = getProgress(currentStepId);

  const cancelModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onStepChange?.();
  }, [currentStepId, onStepChange]);

  useEffect(() => {
    if (!state.showCancelModal) return;

    const modal = cancelModalRef.current;
    if (!modal) return;

    const previouslyFocused = document.activeElement as HTMLElement;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusable[0]?.focus({ preventScroll: true });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus({ preventScroll: true });
    };
  }, [state.showCancelModal]);

  function handleNext(data: Partial<CalculatorFormData>) {
    let updatedData: Partial<CalculatorFormData> = { ...data };

    // Reset partner-dependent fields when has_partner is changed to false,
    // otherwise stale shared_household=true causes the pregnant question to show
    // the partner variant even without a partner.
    if (currentStepId === 'has_partner' && data.has_partner === false) {
      updatedData = {
        ...updatedData,
        shared_household: false,
        income_partner_gross: '',
        income_partner_net: '',
        checkbox_income_partner: false,
      };
    }

    const updatedFormData = { ...state.formData, ...updatedData };
    dispatch({ type: 'UPDATE_FORM', data: updatedData });

    const step = getStepById(currentStepId)!;
    const nextId = resolveNextStep(step, updatedFormData);

    if (nextId === 'result') {
      dispatch({ type: 'SET_LOADING', value: true });
      dispatch({ type: 'SET_API_ERROR', error: null });
      calculateApi(updatedFormData)
        .then(result => {
          dispatch({ type: 'SET_LOADING', value: false });
          onResult(result);
        })
        .catch(error => {
          console.error('[Calculator] Berechnung fehlgeschlagen:', error);
          dispatch({ type: 'SET_API_ERROR', error: texts.summary.calculation_error });
        });
    } else {
      dispatch({ type: 'PUSH_STEP', stepId: nextId });
    }
  }

  function handleBack(data: Partial<CalculatorFormData>) {
    if (Object.keys(data).length > 0) {
      dispatch({ type: 'UPDATE_FORM', data });
    }
    dispatch({ type: 'POP_STEP' });
  }

  function handleCloseRequest() {
    dispatch({ type: 'SHOW_CANCEL_MODAL', value: true });
  }

  function handleCancelConfirm() {
    dispatch({ type: 'SHOW_CANCEL_MODAL', value: false });
    onClose();
  }

  function handleCancelDismiss() {
    dispatch({ type: 'SHOW_CANCEL_MODAL', value: false });
  }

  function renderStep() {
    if (!currentStep) return null;
    const commonProps = {
      formData: state.formData,
      onNext: handleNext,
      onBack: handleBack,
      canGoBack,
      texts,
    };

    switch (currentStep.type) {
      case 'income_fields':
        return <IncomeStep key={currentStep.id} step={currentStep} {...commonProps} />;
      case 'yes_no':
        return <YesNoStep key={currentStep.id} step={currentStep} {...commonProps} />;
      case 'stepper_groups':
        return <StepperGroupsStep key={currentStep.id} step={currentStep} {...commonProps} />;
      case 'multi_income':
        return <MultiIncomeStep key={currentStep.id} step={currentStep} {...commonProps} />;
      case 'housing':
        return <HousingStep key={currentStep.id} step={currentStep} {...commonProps} />;
      case 'child_ages':
        return <ChildAgesStep key={currentStep.id} {...commonProps} />;
      case 'summary':
        return (
          <SummaryStep
            formData={state.formData}
            onConfirm={() => handleNext({})}
            onBack={handleBack}
            onClose={handleCloseRequest}
            isLoading={state.isLoading}
            error={state.apiError ?? undefined}
            texts={texts}
          />
        );
      default:
        return null;
    }
  }

  return (
    <div className={styles.calculator} dir={lang === 'ar' ? 'rtl' : undefined}>
      <div
        className={styles.calculatorCard}
      >
        <div className={styles.calculatorHeader}>
          <div
            className={styles.progressBar}
            role="progressbar"
            aria-valuenow={Math.round(progress * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Fortschritt"
          >
            <div
              className={styles.progressFill}
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleCloseRequest}
            aria-label="Rechner schließen"
          >
            <img
              src={buttonCloseTransparent}
              alt=""
              aria-hidden="true"
              width={14}
              height={14}
            />
          </button>
        </div>

        <div className={styles.calculatorBody}>{renderStep()}</div>
      </div>

      {state.showCancelModal && (
        <div
          className={styles.cancelModalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-modal-title"
        >
          <div className={styles.cancelModal} ref={cancelModalRef}>
            <p id="cancel-modal-title" className={styles.cancelModalTitle}>
              {texts.cancel_modal.title}
            </p>
            <div className={styles.cancelModalActions}>
              <button
                type="button"
                className={styles.btnOutline}
                onClick={handleCancelDismiss}
              >
                {texts.cancel_modal.actions.continue_filling}
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleCancelConfirm}
              >
                {texts.cancel_modal.actions.confirm_cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
